import { Helmet as DefaultHelmet } from "react-helmet";
import { useTranslation } from "@/hooks/useTranslation";
import { useCurrentUrl } from "@/hooks/useCurrentUrl";
import React, { memo, useMemo } from "react";
import isEqual  from 'lodash/isEqual';
import { HOST } from "@/constants/Core";

const DEFAULT_META_DESCRIPTION = 'BMW Teile & Zubehör – Baum BWShop24'

export const Helmet = memo((props) => {

    const { title = null, options = [], meta = {}, children = null } = props;
    const { rawUrlPath } = useCurrentUrl();

    const { t } = useTranslation();
    const translatedTitle = title != null ? t(title, options) : null;

    const childrenArray = React.Children.toArray(children);

    const hasDescMeta = children == null ? false : childrenArray?.some(child => 
        child.type == 'meta' &&
        child.props?.name == "description"
    );
    
    const hasRobotsNoIndex = children == null ? false : childrenArray?.some(child => 
        child.type == 'meta' &&
        child.props?.name == "robots" &&
        String(child.props?.content).toLowerCase().includes("noindex")
    );

    const metas = useMemo(() => {
        if (!meta) return [];

        const response = [];

        // meta kann null/undefined sein
        const metaObj = meta ?? {};
        const keys = Object.keys(metaObj);

        // description: falls nicht gesetzt -> Default verwenden
        const hasDescription =
            Object.prototype.hasOwnProperty.call(metaObj, "description") &&
            metaObj.description != null &&
            String(metaObj.description).trim() !== "";

        const descriptionValueRaw = hasDescription
            ? metaObj.description
            : DEFAULT_META_DESCRIPTION;

        const descriptionValue =
            typeof descriptionValueRaw === "string" &&
            descriptionValueRaw.includes("meta.")
                ? t(descriptionValueRaw)
                : String(descriptionValueRaw);
        
        

        // description + og:description immer setzen (mit Default)
        if (!hasDescMeta) {
            response.push(
                <meta key="description" name="description" content={descriptionValue} />
            );
            response.push(
                <meta
                    key="og:description"
                    property="og:description"
                    content={descriptionValue}
                />
                
            );
        }

        // restliche metas (ohne description, die haben wir schon)
        keys.forEach((key) => {
            if (key === "description") return;

            const raw = metaObj[key];
            const value =
                typeof raw === "string" && raw.includes("meta.") ? t(raw) : raw;

            if (value == null || String(value).trim() === "") return;

            if (key === "robots") {
                response.push(
                    <meta key="robots" name="robots" content={String(value)} />
                );
                return;
            }

            if (key.startsWith("og:")) {
                response.push(
                    <meta key={key} property={key} content={String(value)} />
                );
                return;
            }

            response.push(<meta key={key} name={key} content={String(value)} />);
        });

        return response;
    }, [meta, t, hasDescMeta]);

    const childs = childrenArray?.map(child => {
        
        //if hasRobotsNoIndex remove canonical links, because they contradict each other
        if (hasRobotsNoIndex && child.props?.rel === "canonical") {
            return null;
        }

        return child;
    }) ?? [];  

    return (
        <DefaultHelmet 
            titleTemplate="%s" 
            title={translatedTitle ?? undefined}
            defaultTitle={t('meta.default.title')}
        >
            {/* OG title nur setzen, wenn page-title vorhanden */}
            {translatedTitle != null && (
                <meta property="og:title" content={translatedTitle} />
            )}
            
            {rawUrlPath !== "/" && !childs?.some(child => child.props?.rel == "canonical") && 
                <link rel="canonical" href={HOST + rawUrlPath} />
            }

            {metas}
            {childs}
        </DefaultHelmet>
    );

}, isEqual)